/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { StyledBreadCrumbContainer, StyledLink } from "../../../components/navigable_breadcrumb_link/styles";
import {
  RestaurantGroupContainer,
  StyledButtonContainer,
  StyledCancelButton,
  StyledCreateButton,
  StyledTextField,
  StyledTitle,
  StyledTitleContainer
} from "../../restaurant_groups/styles";
import {
  Box,
  Breadcrumbs,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { StyledIconButton, StyledSubTitle } from "./styles";
import { IndexTable } from "../../../components/index_table";
import camelize from "../../../utils/camelize";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../api";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { StyledAddButton, StyledInputLabel } from "../../network_guests/styles";
import { LoadingSpinner } from "../../../components/loading_spinner";
import { Loader } from "../../../components/loader";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { AbilityContext } from "../../../contexts/abilityContext";
import { strictCanScope } from "../../../utils/caslStrict";
import { RenderMemberAliases } from "../../../components/render_member_aliases";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import { checkAdmin } from "../../../utils/checkAdmin";
import { ConfirmationDialog } from "../../../components/confirmation_dialog";
import CheckIcon from "@mui/icons-material/Check";
import { UpdateButton } from "../../../components/update_button";
import { FilterContainer } from "../../../components/filter_container";
import { selectBaseStyles, viewTypographyStyles } from "../../reports/styles";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import ClearIcon from '@mui/icons-material/Clear';
import { hasSingleItem } from "../../../utils/hasSingleItem";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { formatDate } from "../../../utils/formatDate";
import { RenderDiscountCardsList } from "../../../components/render_discount_cards_list";
import { RenderList } from "../../../components/render_list";
import { ResetButton } from "../../../components/reset_button";
import { Query_Keys } from "../../../lib/queryKeys";
import { DefaultCardSelector } from "./default_card_selector";
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { groupMemberMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/groupMemberMutationKeys";
import { resetPageAfterDeletion } from '../../../utils/resetPageAfterDeletion'
import { useDebounce } from "../../../utils/useDebounce";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeEmployee,
  normalizeRestaurant,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import SearchIcon from "@mui/icons-material/Search";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import authenticatedFetch from "../../../api/authenticatedFetch";
import { formatEmployeeName } from "../../../utils/employeeLabel";

const ALL_VALUE = 'All'
const ALL_GROUP_MEMBERS_PAGE_SIZE = 10000
const defaultFilter = {
  restaurant_ids: [ALL_VALUE],
  added_by_id: ALL_VALUE,
  member_id: ALL_VALUE
}

export const uniquePayBasisValues = (jobs) => {
  const seen = new Set();
  const result = [];

  (jobs || []).forEach((job) => {
    if (!job) return;

    // normalize to an array whether pay_basis is a string or array
    const bases = Array.isArray(job.pay_basis) ? job.pay_basis : job.pay_basis ? [job.pay_basis] : [];

    bases.forEach((b) => {
      if (!b && b !== 0) return;
      const normalized = `${b}`.toString().trim().toLowerCase();
      if (normalized === "") return;
      if (seen.has(normalized)) return;
      seen.add(normalized);
      result.push(`${b}`.toString().trim());
    });
  });

  return result;
}

export const GroupMembers = () => {
  const singleRestaurantAutoApplied = useRef(false)
  const singleEmployeeAutoApplied = useRef(false)
  const { id } = useParams()
  const location = useLocation()
  const { ability } = useContext(AbilityContext)
  const navigate = useNavigate()
  const [openModal, setOpenModal] = useState(false)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const defaultAddMemberPermission = strictCanScope(ability, 'update', 'Restaurant_Group');
  const [addMemberPermission, setAddMemberPermission] = useState(false)
  const queryClient = useQueryClient();
  const showNotification = useNotificationContext();
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [page, setPage] = useState(1)
  const [aliasInputs, setAliasInputs] = useState({})
  const [aliasLoading, setAliasLoading] = useState({})
  const [aliasErrors, setAliasErrors] = useState({})
  const [deleteMemberDialogOpen, setDeleteMemberDialogOpen] = useState(false)
  const [deleteTargetMember, setDeleteTargetMember] = useState('')
  const [deleteMemberLoading, setDeleteMemberLoading] = useState(false)
  const [deleteAliasLoading, setDeleteAliasLoading] = useState(false)
  const [deleteAliasDialogOpen, setDeleteAliasDialogOpen] = useState(false)
  const [deleteTargetAlias, setDeleteTargetAlias] = useState('')
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const [filtersChanged, setFiltersChanged] = useState(false)
  const [sortBy, setSortBy] = useState('added_on')
  const [sortOrder, setSortOrder] = useState('desc')
  const [restaurantSearch, setRestaurantSearch] = useState('')
  const [adminSearch, setAdminSearch] = useState('')
  const [employeeSearch, setEmployeeSearch] = useState('')
  const [filters, setFilters] = useState(defaultFilter)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [defaultCardLoadingMembershipId, setDefaultCardLoadingMembershipId] = useState(null)
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [employeeOptionsCache, setEmployeeOptionsCache] = useState([])
  const [adminUserOptionsCache, setAdminUserOptionsCache] = useState([])
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const debouncedAdminSearch = useDebounce(adminSearch.trim(), 300)
  const debouncedEmployeeSearch = useDebounce(employeeSearch.trim(), 300)
  const debouncedAddMemberSearch = useDebounce(search.trim(), 300)
  const [csvLoading, setCsvLoading] = useState(false)

  useEffect(() => {
    const handlePopState = (event) => {
      navigate("/admin/employee_groups", {
        replace: true,
        state: {
          restaurant_group_id: location?.state?.restaurant_group_id,
          restaurant_group_name: location?.state?.restaurant_group_name,
          parentContext: location?.state?.parentContext,
        }
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate, location]);

  const formattedFilters = (filterState = appliedFilters) => {
    const params = {};

    if (!appliedFilters?.restaurant_ids?.includes(ALL_VALUE)) {
      params.restaurant_id = appliedFilters?.restaurant_ids;
    }

    if (appliedFilters?.added_by_id !== ALL_VALUE) {
      params.added_by_id = appliedFilters?.added_by_id
    }

    if (appliedFilters?.member_id !== ALL_VALUE) {
      params.member_id = appliedFilters?.member_id
    }

    return params;
  }

  const fetchEmployeeGroupMembers = async () => {
    const data = formattedFilters()
    const response = await api.get(`/v1/admin/employee_groups/${id}/group_members`, {
      params: {
        per_page: itemsPerPage,
        page: page,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...data
      }
    })

    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data: groupMembers, isFetching } = useQuery({
    queryKey: Query_Keys.group_members({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
      group_id: id,
    }),
    queryFn: fetchEmployeeGroupMembers,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchAllGroupMemberIds = async () => {
    const response = await api.get(`/v1/admin/employee_groups/${id}/group_members`, {
      params: {
        per_page: ALL_GROUP_MEMBERS_PAGE_SIZE,
        page: 1,
      }
    })

    return response?.data?.map((record) => record?.member?.id) || []
  }

  const { data: existingMemberIds = [] } = useQuery({
    queryKey: Query_Keys.group_members({ group_id: id, allMemberIds: true }),
    queryFn: fetchAllGroupMemberIds,
    staleTime: 10 * 60 * 1000,
    enabled: openModal && !!id,
  })

  const fetchEmployeeGroup = useCallback(async () => {
    const response = await api.get(`/v1/admin/employee_groups/${id}`)
    return response?.data
  }, [])

  const { data: employeeGroupData } = useQuery({
    queryKey: Query_Keys.employee_group({
      group_id: id
    }),
    queryFn: fetchEmployeeGroup,
    staleTime: 10 * 60 * 1000,
  })

  const fetchEmployees = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/active_users_list', {
      params: {
        restaurant_group_id: employeeGroupData?.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedAddMemberSearch || undefined,
      }
    })
    return response?.data || []
  }, [debouncedAddMemberSearch, employeeGroupData?.restaurant_group_id])

  const {
    data: employees = [],
    isFetching: isEmployeesFetching,
    isFetchingNextPage: employeesFetchingNextPage,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.active_users_list({
      restaurant_group_id: employeeGroupData?.restaurant_group_id,
      search: debouncedAddMemberSearch,
    }),
    queryFn: fetchEmployees,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    enabled: openModal && !!employeeGroupData?.restaurant_group_id,
    placeholderData: keepPreviousData,
  })

  const fetchEmployeesList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/active_guests_list", {
      params: {
        restaurant_group_id: employeeGroupData?.restaurant_group_id,
        restaurant_id: filters?.restaurant_ids?.includes(ALL_VALUE) ? undefined : filters?.restaurant_ids,
        page: pageParam,
        per_page: PerPage,
        search: debouncedEmployeeSearch || undefined,
      }
    });
    return response?.data?.map(normalizeEmployee) || [];
  };

  const {
    data: employeesList = [],
    isFetching: employeeFetching,
    isFetchingNextPage: employeesListFetchingNextPage,
    fetchNextPage: fetchNextEmployeesListPage,
    hasNextPage: hasNextEmployeesListPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.active_guests_list({
      restaurant_group_id: employeeGroupData?.restaurant_group_id,
      restaurant_id: filters?.restaurant_ids?.includes(ALL_VALUE) ? undefined : filters?.restaurant_ids,
      search: debouncedEmployeeSearch,
    }),
    queryFn: fetchEmployeesList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    enabled: !!employeeGroupData?.restaurant_group_id,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const res = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        restaurant_group_ids: employeeGroupData?.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
      }
    });
    return res?.data?.map(normalizeRestaurant) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: employeeGroupData?.restaurant_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    enabled: !!employeeGroupData?.restaurant_group_id,
    placeholderData: keepPreviousData,
  });

  const fetchAdminUsers = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/admin_app_users_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedAdminSearch || undefined,
      }
    })
    return response?.data?.map(normalizeEmployee) || []
  }

  const {
    data: adminUsers = [],
    isFetching: adminUserFetching,
    isFetchingNextPage: adminUsersFetchingNextPage,
    fetchNextPage: fetchNextAdminUsersPage,
    hasNextPage: hasNextAdminUsersPage,
  } = useInfiniteQuery({
    queryKey: [...Query_Keys.admin_user_dropdown, { search: debouncedAdminSearch }],
    queryFn: fetchAdminUsers,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const { rootRef: employeeMenuRef, targetRef: employeeLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextEmployeesListPage,
    hasNextPage: hasNextEmployeesListPage,
    isFetchingNextPage: employeesListFetchingNextPage,
  })

  const { rootRef: adminUserMenuRef, targetRef: adminUserLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextAdminUsersPage,
    hasNextPage: hasNextAdminUsersPage,
    isFetchingNextPage: adminUsersFetchingNextPage,
  })

  const { rootRef: addMemberModalRootRef, targetRef: addMemberLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
    isFetchingNextPage: employeesFetchingNextPage,
  })

  useEffect(() => {
    setAddMemberPermission(strictCanScope(ability, 'update', 'Restaurant_Group', `${employeeGroupData?.restaurant_group_id}`))
  }, [ability, employeeGroupData])

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'))
  }, [restaurants])

  useEffect(() => {
    setEmployeeOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, employeesList, 'value'))
  }, [employeesList])

  useEffect(() => {
    setAdminUserOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, adminUsers, 'value'))
  }, [adminUsers])

  useEffect(() => {
    if (employeeOptionsCache && filters?.member_id) {
      const availableEmployees = employeeOptionsCache?.map(employee => employee?.value);

      if(!(availableEmployees?.includes(filters?.member_id))) {
        setFilters(prev => ({ ...prev, member_id: ALL_VALUE }))
      }
    }
  }, [employeeOptionsCache, filters?.member_id]);

  useEffect(() => {
    if (
      !filters?.restaurant_ids?.includes(ALL_VALUE)
      || singleRestaurantAutoApplied.current
      || debouncedRestaurantSearch
      || !hasSingleItem(restaurants)
    ) {
      return
    }

    const singleRestaurant = getSingleItemValue(restaurants);
    setFilters(prev => ({
      ...prev,
      restaurant_ids: [singleRestaurant.id]
    }));
    singleRestaurantAutoApplied.current = true
  }, [debouncedRestaurantSearch, filters?.restaurant_ids, restaurants]);

  useEffect(() => {
    if (
      filters?.member_id !== ALL_VALUE
      || singleEmployeeAutoApplied.current
      || debouncedEmployeeSearch
      || !hasSingleItem(employeesList)
    ) {
      return
    }

    const singleEmployee = getSingleItemValue(employeesList);
    setFilters(prev => ({
      ...prev,
      member_id: singleEmployee.value
    }));
    singleEmployeeAutoApplied.current = true
  }, [debouncedEmployeeSearch, employeesList, filters?.member_id]);

  const { mutateAsync: addGroupMembersMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post(`/v1/admin/employee_groups/${employeeGroupData?.id}/add_members`, payload),
    invalidateKeys: [...groupMemberMutationKeyGroups.create],
    successMessage: () => "Selected Members have been added successfully",
    errorMessage: () => 'Failed to add Members',
    onSuccess: () => {
      setOpenModal(false);
      setSelectedEmployees([]);
    },
    onSettled: () => setLoading(false)
  });

  const handleAddMembers = async () => {
    setLoading(true)

    try {
      await addGroupMembersMutation({
        employee_ids: selectedEmployees?.map((emp) => emp?.id)
      });
    } catch {
      // Handled in onError
    }
  }

  const { mutateAsync: addMemberAliasMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/employee_groups/add_alias', payload),
    invalidateKeys: [...groupMemberMutationKeyGroups.update],
    successMessage: () => "Alias has been added successfully",
    onSuccess: (data, variables) => {
      setAliasInputs((prev) => ({ ...prev, [variables.membership_id]: '' }))
    },
    onError: (error, variables) => {
      setAliasErrors((prev) => ({ ...prev, [variables.membership_id]: error?.response?.data?.errors || {} }));
    },
    onSettled: (_, error, variables) => {
      setAliasLoading((prev) => ({ ...prev, [variables.membership_id]: false }));
    }
  });

  const handleAddAlias = async (membershipId) => {
    if (!aliasInputs[membershipId]?.trim()) {
      setAliasErrors(prev => ({ ...prev, [membershipId]: { alias_raw: ["Alias can't be blank"] } }));
      return;
    }

    setAliasLoading(prev => ({ ...prev, [membershipId]: true }))

    try {
      await addMemberAliasMutation({
        membership_id: membershipId,
        alias_raw: aliasInputs[membershipId]
      });
    } catch {
      // Handled in onError
    }
  }

  const { mutateAsync: deleteGroupMemberMutation } = useOtMutation({
    mutationFn: async (params) => await api.delete(`/v1/admin/employee_groups/${id}/remove_member`, { params }),
    invalidateKeys: [...groupMemberMutationKeyGroups.delete],
    successMessage: () => "Member has been deleted successfully",
    errorMessage: ({ error }) => {
      const errors = error?.response?.data?.errors;
      return errors?.base?.[0] || 'Failed to delete member';
    },
    onSuccess: () => {
      setDeleteMemberDialogOpen(false)
      resetPageAfterDeletion({ totalCount: groupMembers?.pageCount, removed: 1, page, itemsPerPage, setPage })
    },
    onError: () => {
      setDeleteMemberDialogOpen(false);
    },
    onSettled: () => setDeleteMemberLoading(false)
  });

  const handleDeleteMember = async () => {
    setDeleteMemberLoading(true);

    try {
      await deleteGroupMemberMutation({
        network_guest_id: deleteTargetMember?.id
      });
    } catch {
      // Handled in onError
    }
  }

  const { mutateAsync: deleteMemberAliasMutation } = useOtMutation({
    mutationFn: async () => await api.delete(`/v1/admin/employee_groups/${id}/aliases/${deleteTargetAlias?.id}`),
    invalidateKeys: [...groupMemberMutationKeyGroups.delete],
    successMessage: () => "Alias has been deleted successfully",
    errorMessage: ({ error }) => {
      const errors = error?.response?.data?.errors;
      return errors?.base?.[0] || 'Failed to delete alias';
    },
    onSuccess: () => {
      setDeleteAliasDialogOpen(false);
    },
    onError: () => {
      setDeleteAliasDialogOpen(false);
    },
    onSettled: () => setDeleteAliasLoading(false)
  });

  const handleDeleteAlias = async () => {
    setDeleteAliasLoading(true);

    try {
      await deleteMemberAliasMutation();
    } catch {
      // Handled in onError
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSearch('')
    setSelectedEmployees([])
  }

  const handleDeleteMemberClick = (record) => {
    setDeleteTargetMember(record?.member)
    setDeleteMemberDialogOpen(true)
  }

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurant_ids || filters?.restaurant_ids?.length === 0) {
      return 'All';
    }
    if (filters?.restaurant_ids?.includes(ALL_VALUE)) {
      return 'All';
    }
    if (filters?.restaurant_ids?.length === 1) {
      const restaurant = restaurantOptionsCache.find(r => r.id === filters.restaurant_ids[0]);
      return restaurant ? restaurant.name : 'All';
    }
    return `${filters?.restaurant_ids?.length} Restaurants Selected`;
  }

  const getAdminUserName = (id) => {
    const name = adminUserOptionsCache?.find((user) => user.value === id)
    return name?.label ? name?.label : ALL_VALUE
  }

  const getEmployeeName = (id) => {
    if (id === ALL_VALUE) return 'All';
    const employee = employeeOptionsCache?.find((emp) => emp.value === id)
    return employee ? employee?.label : ''
  }

  const handleRestaurantDropDownChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltersChanged(true);

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters(prev => ({ ...prev, restaurant_ids: [ALL_VALUE] }));
    }
    else {
      const newRestaurantIds = value.filter(val => val !== ALL_VALUE);
      setFilters(prev => ({ ...prev, restaurant_ids: newRestaurantIds }));
    }
  }

  const handleAdminUserChange = (event) => {
    setFilters(prev => ({ ...prev, added_by_id: event.target.value }))
    setFiltersChanged(true)
  }

  const handleEmployeeChange = (event) => {
    setFilters(prev => ({ ...prev, member_id: event.target.value }));
    setFiltersChanged(true);
  }

  const handleEmployeeClear = () => {
    setFilters(prev => ({ ...prev, member_id: ALL_VALUE }));
    setFiltersChanged(true);
  }

  const handleRestaurantClear = () => {
    setFiltersChanged(true);
    setFilters(prev => ({ ...prev, restaurant_ids: [ALL_VALUE] }));
  }

  const handleAdminUserClear = () => {
    setFilters(prev => ({ ...prev, added_by_id: ALL_VALUE }))
    setFiltersChanged(true)
  }

  const handleUpdate = () => {
    setItemsPerPage(25);
    setPage(1);
    setAppliedFilters(filters)
    setFiltersChanged(false)
  }

  const handleSortChange = (columnKey, direction) => {
    setSortBy(columnKey)
    setSortOrder(direction)
    setPage(1)
  }

  const handleReset = () => {
    setFiltersChanged(false)
    setFilters(defaultFilter)
    setAppliedFilters(defaultFilter)
    setItemsPerPage(25);
    setPage(1);
  }

  const availableEmployees = employees?.filter((emp) => !existingMemberIds?.includes(emp.id));

  const handleToggle = (employee) => {
    setSelectedEmployees((prev) => {
      const currentIndex = prev.findIndex((e) => e.id === employee.id);

      if (currentIndex === -1) {
        return [...prev, employee];
      }

      const updated = [...prev];
      updated.splice(currentIndex, 1);
      return updated;
    });
  }

  const handleDeleteAliasClick = (record) => {
    setDeleteTargetAlias(record)
    setDeleteAliasDialogOpen(true)
  }

  const getPermission = (action) => {
    if (superAdmin?.role === 'admin') {
      return true
    }

    return strictCanScope(ability, action, 'Restaurant_Group', `${employeeGroupData?.restaurant_group_id}`)
  }

  const getErrorMessage = (errors, fallback) => {
    const firstKey = Object.keys(errors || {})?.[0]
    return errors?.[firstKey]?.[0] || fallback
  }

  const getAssignedDiscountCardId = (card) => card?.card_id ?? card?.id

  const getAssignedDiscountCardOptionKey = (card, index) => (
    `${getAssignedDiscountCardId(card)}-${card?.restaurant_id ?? index}`
  )

  const getCurrentDefaultCardOptionKey = (record) => {
    const assignedCards = record?.member?.assigned_discount_cards || []
    const currentDefaultCardId = record?.default_card?.id

    if (!currentDefaultCardId) return null

    const selectedIndex = assignedCards.findIndex(
      (card) => getAssignedDiscountCardId(card) === currentDefaultCardId
    )

    if (selectedIndex === -1) return null

    return getAssignedDiscountCardOptionKey(assignedCards[selectedIndex], selectedIndex)
  }

  const handleDefaultCardUpdate = async (record, discountCard) => {
    const selectedCardId = getAssignedDiscountCardId(discountCard)
    const currentOptionKey = getCurrentDefaultCardOptionKey(record)
    const selectedOptionKey = getAssignedDiscountCardOptionKey(
      discountCard,
      record?.member?.assigned_discount_cards?.findIndex((card) => card?.id === discountCard?.id)
    )

    if (!record?.membership_id || !selectedCardId) return false

    if (currentOptionKey === selectedOptionKey) {
      return false
    }

    if (record?.default_card?.id === selectedCardId) {
      showNotification(
        'This default card is stored by card only, so switching the same card between restaurants is not supported yet.',
        'warning'
      )

      return false
    }

    setDefaultCardLoadingMembershipId(record?.membership_id)

    try {
      const response = await api.put(`/v1/admin/employee_groups/${id}/assign_default_card`, {
        employee_group_membership_id: record?.membership_id,
        discount_card_id: selectedCardId,
      })

      queryClient.invalidateQueries({ queryKey: ["group_members"] })
      showNotification(response?.data?.message || 'Default card updated successfully', 'success')
      return true
    } catch (error) {
      showNotification(
        getErrorMessage(error?.response?.data?.errors, 'Failed to update default card'),
        'error'
      )
      return false
    } finally {
      setDefaultCardLoadingMembershipId(null)
    }
  }

  const handleExportCsv = useCallback(async () => {
    try {
      setCsvLoading(true)
      const data = formattedFilters(appliedFilters)
      const exportParams = {
        id: id,
        ...data,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      }
      const query = buildQueryParams(exportParams)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=employee_group_members&${query}`

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      })

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      const date = new Date().toISOString().slice(0, 10)
      a.download = `employee_group_members_${date}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(downloadUrl)
      showNotification('CSV downloaded successfully', 'success')
    } catch (error) {
      console.error('CSV download error:', error)
      showNotification(error?.message || 'Failed to download CSV', 'error')
    } finally {
      setCsvLoading(false)
    }
  }, [appliedFilters, sortBy, sortOrder])

  const TableColumns = [
    { key: "id", sortable: true },
    { key: "name", sortable: true },
    { key: "aliases", sortable: true },
    { key: "add_alias", sortable: false, width: 250, minWidth: 210 },
    { key: "email", sortable: true },
    { key: "phone_number", sortable: true },
    { key: "discount_cards", label: "Perq Cards", sortable: true },
    { key: "default_card", sortable: true },
    { key: "restaurant", sortable: true },
    { key: "job_descriptions", sortable: true },
    { key: "pay_basis", sortable: true },
    { key: "added_on", sortable: true },
    { key: "added_by", sortable: true },
    { key: "actions", sortable: false }
  ]

  const columns = TableColumns?.map((column) => {
    return {
      key: column.key,
      label: column.label || camelize(column.key),
      width: column.width,
      minWidth: column.minWidth,
      sortable: column.sortable,
    }
  })

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.member?.id,
        name: `${record?.member?.first_name} ${record?.member?.last_name}`,
        email: record?.member?.email,
        phone_number: record?.member?.phone_number,
        discount_cards: {
          display: (
            <RenderDiscountCardsList data={record?.member?.assigned_discount_cards} />
          ),
          sortValue: record?.member?.assigned_discount_cards
        },
        default_card: {
          display: (
            <DefaultCardSelector
              membershipId={record?.membership_id}
              card={record?.default_card}
              assignedCards={record?.member?.assigned_discount_cards}
              canUpdate={getPermission('update')}
              isLoading={defaultCardLoadingMembershipId === record?.membership_id}
              onUpdate={(discountCard) => handleDefaultCardUpdate(record, discountCard)}
            />
          ),
          sortValue: record?.default_card?.name
        },
        restaurant: {
          display: (
            <RenderList data={record?.member?.created?.map((res) => res?.restaurant?.name)} />
          ),
          sortValue: record?.member?.created?.[0]?.restaurant?.name
        },
        job_descriptions: {
          display: (
            <RenderList data={record?.member?.job_descriptions?.map((job) => job.title)} />
          ),
          sortValue: record?.member?.job_descriptions?.[0]?.title
        },
        pay_basis: {
          display: (
            <RenderList data={uniquePayBasisValues(record?.member?.job_descriptions)} />
          ),
          sortValue: uniquePayBasisValues(record?.member?.job_descriptions)?.[0]
        },
        aliases: {
          display: (
            <RenderMemberAliases
              data={record?.aliases}
              handleDelete={handleDeleteAliasClick}
              canDelete={getPermission('update')}
            />
          ),
          sortValue: record?.aliases?.length
        },
        add_alias: (
          <Box>
            <StyledTextField
              size="small"
              placeholder="e.g. aholder"
              error={aliasErrors[record?.membership_id]}
              helperText={aliasErrors[record?.membership_id]?.alias_raw?.[0]}
              value={aliasInputs[record?.membership_id] || ''}
              onChange={(e) => {
                setAliasInputs((prev) => ({ ...prev, [record?.membership_id]: e.target.value }))
                setAliasErrors((prev) => ({ ...prev, [record?.membership_id]: '' }))
              }}
              sx={{
                "& .MuiInputBase-input": {
                  width: "130px"
                }
              }}
            />
            <StyledIconButton
              size="small"
              onClick={() => handleAddAlias(record?.membership_id)}
              data-testid="add-button"
              disabled={!getPermission('update')}
            >
              {aliasLoading[record?.membership_id] ?
                <LoadingSpinner isLoading={true} /> :
                <AddIcon
                  sx={{
                    color: getPermission('update') ? "#707070" : "#e0e0e0",
                    cursor: "pointer",
                  }}
                />}
            </StyledIconButton>
          </Box>
        ),
        added_on: formatDate(record?.created_at, record?.time_zone_abbr),
        added_by: record?.created_by,
        actions: (
          <Tooltip title="Delete Member" arrow >
            <span>
              <IconButton
                data-testid="delete-button"
                disabled={!getPermission('update')}
                onClick={() => handleDeleteMemberClick(record)}
              >
                <DeleteIcon
                  sx={{
                    color: getPermission('update') ? "#707070" : "#e0e0e0",
                    cursor: "pointer",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )
      }
    })
  }

  const rows = TableRows(groupMembers?.data);

  return (
    <RestaurantGroupContainer
      style={{
        width: '100%'
      }}
    >
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink 
            LinkComponent={StyledLink} 
            label="Home" to="/admin/dashboard" 
          />
          <NavigableBreadcrumbLink
            LinkComponent={StyledLink}
            label="Employee Groups"
            to="/admin/employee_groups"
            state={{
              restaurant_group_id: employeeGroupData?.restaurant_group_id,
              restaurant_group_name: location?.state?.restaurant_group_name,
              parentContext: location?.state?.parentContext,
            }}
          />

          <StyledLink color={'#515152'}>
            {employeeGroupData?.name}
          </StyledLink>

        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <FilterContainer>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: "18px" }}>
            <Typography
              variant="body2"
              sx={{
                ...viewTypographyStyles,
                alignSelf: { xs: "flex-start", sm: "center" },
                whiteSpace: "nowrap",
                mt: 0
              }}
            >
              View
            </Typography>

            <FormControl size="small">
              <StyledInputLabel>Restaurant</StyledInputLabel>
              <Select
                size="small"
                label="Restaurant"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                  autoFocus: false,
                  MenuListProps: { sx: { pt: 0 } },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "140px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantDropDownChange}
                onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                value={(!debouncedRestaurantSearch && hasSingleItem(restaurants))
                  ? [getSingleItemValue(restaurants)?.id]
                  : (filters?.restaurant_ids || [ALL_VALUE])}
                multiple
                renderValue={() => {
                  if (!debouncedRestaurantSearch && hasSingleItem(restaurants)) {
                    return getSingleItemValue(restaurants)?.name || 'All';
                  }
                  return getRestaurantDisplayValue();
                }}
                endAdornment={
                  !filters?.restaurant_ids?.includes(ALL_VALUE) && !(!debouncedRestaurantSearch && hasSingleItem(restaurants)) ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                {/* All option */}
                {!(!debouncedRestaurantSearch && restaurants?.length === 1) && (<StyledMenuItem
                  key={ALL_VALUE}
                  value={ALL_VALUE}
                  disabled={!filters?.restaurant_ids?.includes(ALL_VALUE)}
                >
                  {filters?.restaurant_ids?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                )}
                {restaurants?.map((res) => (
                  <StyledMenuItem
                    key={res?.id}
                    value={res?.id}
                  >
                    {
                      (!debouncedRestaurantSearch && hasSingleItem(restaurants)) || filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantsPage}
                  targetRef={restaurantLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl size="small">
              <StyledInputLabel>Employee Name</StyledInputLabel>
              <Select
                value={(!debouncedEmployeeSearch && hasSingleItem(employeesList))
                  ? getSingleItemValue(employeesList)?.value
                  : (filters?.member_id || ALL_VALUE)}
                renderValue={() => {
                  if (!debouncedEmployeeSearch && hasSingleItem(employeesList)) {
                    return getSingleItemValue(employeesList)?.label || 'All';
                  }
                  return getEmployeeName(filters?.member_id);
                }}
                label="Employee Name"
                size="small"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: { ref: employeeMenuRef, sx: { maxHeight: "40vh" } },
                  autoFocus: false,
                  MenuListProps: { sx: { pt: 0 } },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: 120,
                  width: 160,
                  borderColor: "#2b4fb9",
                }}
                onChange={handleEmployeeChange}
                onClose={() => setTimeout(() => setEmployeeSearch(''), 1000)}
                endAdornment={
                  filters?.member_id !== ALL_VALUE && !(!debouncedEmployeeSearch && hasSingleItem(employeesList)) ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleEmployeeClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setEmployeeSearch} placeholder={'Search employee'} isFetching={employeeFetching} />
                {!(!debouncedEmployeeSearch && employeesList?.length === 1) && (<StyledMenuItem
                  key={ALL_VALUE}
                  value={ALL_VALUE}
                >
                  {filters?.member_id === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                )}
                {employeesList
                  ?.map((employee) => (
                    <StyledMenuItem
                      key={employee.value}
                      value={employee.value}
                    >
                      {
                        (!debouncedEmployeeSearch && hasSingleItem(employeesList)) || filters?.member_id === employee.value ? <SelectedMenuItem data={employee?.label} /> : employee?.label
                      }
                    </StyledMenuItem>
                  ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextEmployeesListPage}
                  targetRef={employeeLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl size="small">
              <StyledInputLabel>Added by admin</StyledInputLabel>
              <Select
                value={filters?.added_by_id || ALL_VALUE}
                renderValue={() =>
                  getAdminUserName(filters?.added_by_id)
                }
                size="small"
                label="Added by admin"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: { ref: adminUserMenuRef, sx: { maxHeight: "40vh" } },
                  autoFocus: false,
                  MenuListProps: { sx: { pt: 0 } },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: 120,
                  borderColor: "#2b4fb9",
                }}
                onClose={() => setTimeout(() => setAdminSearch(''), 1000)}
                onChange={handleAdminUserChange}
                endAdornment={
                  filters?.added_by_id !== ALL_VALUE ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleAdminUserClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setAdminSearch} placeholder={'Search admin user'} isFetching={adminUserFetching} />
                {/* All option */}
                {!(!debouncedAdminSearch && adminUsers?.length === 1) && (<StyledMenuItem
                  key={ALL_VALUE}
                  value={ALL_VALUE}
                >
                  {filters?.added_by_id === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                )}
                {adminUsers?.map((user) => (
                  <StyledMenuItem
                    key={user?.value}
                    value={user?.value}
                  >
                    {
                      filters?.added_by_id === user?.value ? <SelectedMenuItem data={user?.label} /> : user?.label
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextAdminUsersPage}
                  targetRef={adminUserLoaderRef}
                />
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              alignSelf: { xs: "flex-start", md: "center" },
              mt: { xs: 1, md: 0 },
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "flex-start", md: "flex-end" }
            }}
          >
            <ResetButton
              onClick={handleReset}
              status={isFetching}
            />

            <UpdateButton
              onClick={handleUpdate}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </Box>
      </FilterContainer>

      <StyledTitleContainer>
        <StyledTitle>{employeeGroupData?.name} - Members & Aliases</StyledTitle>
        <StyledSubTitle>Group ID: {employeeGroupData?.group_id}</StyledSubTitle>
      </StyledTitleContainer>

      <Divider style={{ borderStyle: 'solid', borderWidth: '1px' }} />

      <div style={{ padding: '20px 0px' }}>
        <StyledCreateButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
          disableRipple
          disabled={!(defaultAddMemberPermission || addMemberPermission)}
        >
          Add Member
        </StyledCreateButton>
      </div>

      {isFetching ? (
        <Box data-testid="loader" >
          <Loader />
        </Box>
      ) : (
        <IndexTable
          columns={columns}
          data={rows}
          tableId={"members"}
          type={"entries"}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          pageCount={groupMembers?.pageCount}
          report={false}
          sortMode="server"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          showDownloadButton={true}
          disableDownloadButton={!groupMembers?.data?.length || csvLoading}
          handleExportCsv={handleExportCsv}
          csvLoading={csvLoading}
        />)}

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        transitionDuration={0}
        PaperProps={{
          sx: {
            height: "90vh",
            display: "flex",
            flexDirection: "column",
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Select Employees</DialogTitle>

        <div style={{ padding: "0 24px 16px 24px" }}>
          <StyledTextField
            fullWidth
            placeholder="Search employees..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            InputProps={{
              sx: { borderRadius: "20px" },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ justifyContent: "center", minWidth: "20px" }}
                >
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      height: "20px",
                      justifyContent: "center",
                      width: "20px",
                    }}
                  >
                    <LoadingSpinner isLoading={isEmployeesFetching} />
                  </Box>
                </InputAdornment>
              )
            }}
          />
        </div>

        <Divider />

        <DialogContent
          ref={addMemberModalRootRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            pt: 1,
          }}
        >
          {!isEmployeesFetching && availableEmployees?.length === 0 ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6B7280",
                  fontSize: "14px",
                }}
              >
                No employees found
              </div>
            ) : (
              <List>
                {availableEmployees?.map((employee) => (
                  <ListItem
                    key={employee.id}
                    sx={{ padding: '4px 10px' }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedEmployees.some((e) => e.id === employee.id)}
                        onClick={(e) => { e.stopPropagation(); handleToggle(employee); }}
                        disableRipple
                        disableTouchRipple
                        sx={{ transition: 'none' }}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary={employee?.full_name}
                      secondary={<React.Fragment>
                        <div>{`Phone: ${employee?.phone_number}`}</div>
                        <div>{`Active Restaurants: ${employee?.restaurants?.map((res) => res?.name)?.join(', ')}`}</div>
                      </React.Fragment>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          <DropdownInfiniteLoader
            hasNextPage={hasNextEmployeesPage}
            targetRef={addMemberLoaderRef}
          />
        </DialogContent>

        <Divider />

        <StyledButtonContainer sx={{ padding: "16px 24px" }}>
          <StyledCancelButton
            variant="outlined"
            onClick={handleCloseModal}
            disableRipple
          >
            Cancel
          </StyledCancelButton>

          <StyledAddButton
            variant="contained"
            onClick={handleAddMembers}
            disableRipple
            disabled={!selectedEmployees.length}
          >
            {loading ? <LoadingSpinner isLoading={true} /> : 'Add'}
          </StyledAddButton>
        </StyledButtonContainer>
      </Dialog>

      <ConfirmationDialog
        open={deleteMemberDialogOpen}
        onClose={() => setDeleteMemberDialogOpen(false)}
        Icon={CheckIcon}
        dialogTitle="Are you sure you want to delete the member?"
        dialogBody={`This action will remove ${formatEmployeeName(deleteTargetMember, { fallback: 'Unnamed Member' })} from employee group: ${employeeGroupData?.name}`}
        confirmationText="Yes, I want to delete the member"
        handleConfirm={handleDeleteMember}
        isLoading={deleteMemberLoading}
      />

      <ConfirmationDialog
        open={deleteAliasDialogOpen}
        onClose={() => setDeleteAliasDialogOpen(false)}
        Icon={CheckIcon}
        dialogTitle="Are you sure you want to delete the alias?"
        dialogBody={`This action will remove the alias: ${deleteTargetAlias?.alias_raw} from the associated member.`}
        confirmationText="Yes, I want to delete the alias"
        handleConfirm={handleDeleteAlias}
        isLoading={deleteAliasLoading}
      />
    </RestaurantGroupContainer>
  )
}
